import React from 'react';
import { NavLink, Outlet, useLocation, Link } from 'react-router-dom';
import { Home, Salad, MessageCircle, User2, ClipboardList } from 'lucide-react';
import './mobile.css';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { Account } from '../app/services/Account';
import { PlayFab } from '../app/services/PlayFab';

function TabItem({ to, label, icon: Icon, large }: { to: string; label: string; icon: any; large?: boolean }) {
  const location = useLocation();
  const isActive = location.pathname === to;
  const [pressing, setPressing] = React.useState(false);

  const onDown = () => {
    setPressing(true);
    try {
      // Haptic-like feedback on supported devices
      (navigator as any)?.vibrate?.(12);
    } catch {}
  };
  const onUp = () => setPressing(false);
  const onLeave = () => setPressing(false);

  return (
    <NavLink
      to={to}
      className={`mb-tab ${isActive ? 'active' : ''} ${large ? 'large' : ''} ${pressing ? 'pressing' : ''}`}
      aria-label={label}
      onPointerDown={onDown}
      onPointerUp={onUp}
      onPointerLeave={onLeave}
    >
      <Icon className="mb-tab-icon" />
      {!large && <span className="mb-tab-label">{label}</span>}
    </NavLink>
  );
}

export default function MobileLayout() {
  const AUTH0_DOMAIN = 'auth.gymbuddy.io';
  const AUTH0_CLIENT_ID = 'TH4y2zz3JdmTBUBXRLLIjaj1E4IakAAa';

  return (
    <Auth0Provider
      domain={AUTH0_DOMAIN}
      clientId={AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: `${window.location.origin}/auth0`,
        scope: 'openid profile'
      }}
    >
      <MobileLayoutInner />
    </Auth0Provider>
  );
}

function MobileLayoutInner() {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [displayName, setDisplayName] = React.useState('');
  const [level, setLevel] = React.useState<number>(0);
  const [xp, setXp] = React.useState<number>(0);
  const { isAuthenticated, isLoading, loginWithRedirect, getIdTokenClaims, user, logout } = useAuth0();

  React.useEffect(() => {
    async function syncAuth() {
      if (isLoading) return;
      if (isAuthenticated && !PlayFab.hasValidSession()) {
        try {
          const claims: any = await getIdTokenClaims();
          if (claims && claims.__raw) {
            const result = await Account.continueLoginWithAuth0(claims.__raw, user);
            if (!result.success) return;
          }
        } catch (e) {
          return;
        }
      }

      if (PlayFab.hasValidSession()) {
        try {
          const profile = await PlayFab.getPlayerProfile();
          const name = profile?.DisplayName || Account.getUserName();
          setDisplayName(name || '');

          const data = await PlayFab.getUserReadOnlyData(['XP']);
          let total = 0;
          if (data.success && data.data?.XP?.Value) {
            try {
              const parsed = JSON.parse(data.data.XP.Value);
              total = parsed.value || 0;
            } catch {
              total = parseInt(data.data.XP.Value) || 0;
            }
          }
          const lvl = Math.floor(total / 100) + 1;
          const cur = total % 100;
          setLevel(lvl);
          setXp(cur);
        } catch {}
      }
    }
    syncAuth();
  }, [isAuthenticated, isLoading, getIdTokenClaims, user]);

  const loggedIn = PlayFab.hasValidSession() || isAuthenticated;

  return (
    <div className="mb-root">
      <div className="mb-shell">
        <div className="mb-topbar">
          <div className="mb-brand">
            <img className="mb-logo" src="/images/GymBuddy.png" alt="GymBuddy" onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/images/PokerBuddy.png'; }} />
            <span className="mb-title">GymBuddy</span>
            <span className="mb-badge">Dark</span>
          </div>
          <div className="mb-streak">
            <span className="mb-flame">🔥</span>
            <span>1</span>
            {loggedIn ? (
              <div className="mb-user">
                <div className="mb-user-name">{displayName || '...'}</div>
                <div className="mb-xp">
                  <div className="mb-xp-bar"><div className="mb-xp-fill" style={{ width: `${(xp / 100) * 100}%` }} /></div>
                  <div className="mb-xp-text">Lv {level} · {xp}/100</div>
                </div>
              </div>
            ) : (
              <button className="mb-menu-btn" onClick={() => loginWithRedirect({ authorizationParams: { redirect_uri: `${window.location.origin}/auth0` } })}>Login</button>
            )}
            <button className="mb-menu-btn" onClick={() => setMenuOpen(true)} aria-label="Open menu">☰</button>
          </div>
        </div>
        <div className="mb-content">
          <div className="mb-content-inner">
            <div className="mb-content-header">
              <div className="mb-content-title">Today</div>
              <div className="mb-content-sub">Novice plan</div>
            </div>
            <div className="mb-outlet">
              <Outlet />
            </div>
          </div>
          <div className="mb-bottomnav">
            <TabItem to="/mobile" label="Home" icon={Home} />
            <TabItem to="/mobile/macros" label="Macros" icon={Salad} />
            <TabItem to="/mobile/chat" label="Chat" icon={MessageCircle} large />
            <TabItem to="/mobile/physique" label="Physique" icon={User2} />
            <TabItem to="/mobile/program" label="Program" icon={ClipboardList} />
          </div>
        </div>
        {menuOpen && (
          <div className="mb-overlay" onClick={() => setMenuOpen(false)}>
            <div className="mb-overlay-panel" onClick={(e) => e.stopPropagation()}>
              <div className="mb-overlay-header">
                <div className="mb-overlay-title">Site menu</div>
                <button className="mb-menu-close" onClick={() => setMenuOpen(false)} aria-label="Close menu">✕</button>
              </div>
              <div className="mb-overlay-list">
                {!loggedIn && (
                  <button className="mb-btn" onClick={() => loginWithRedirect({ authorizationParams: { redirect_uri: `${window.location.origin}/auth0` } })}>Login</button>
                )}
                {loggedIn && (
                  <button className="mb-btn" onClick={() => { Account.logout(); logout({ logoutParams: { returnTo: window.location.origin + '/logout' } }); setDisplayName(''); setLevel(0); setXp(0); }}>Logout</button>
                )}
                <Link to="/" className="mb-overlay-link" onClick={() => setMenuOpen(false)}>Legacy Home</Link>
                <Link to="/buddy" className="mb-overlay-link" onClick={() => setMenuOpen(false)}>Submit Hands</Link>
                <Link to="/handnotes" className="mb-overlay-link" onClick={() => setMenuOpen(false)}>Hand Notes</Link>
                <Link to="/visualiser" className="mb-overlay-link" onClick={() => setMenuOpen(false)}>Visualiser</Link>
                <Link to="/hand-review" className="mb-overlay-link" onClick={() => setMenuOpen(false)}>Hand Review</Link>
                <Link to="/profile" className="mb-overlay-link" onClick={() => setMenuOpen(false)}>Profile</Link>
                <Link to="/charts" className="mb-overlay-link" onClick={() => setMenuOpen(false)}>Charts</Link>
                <Link to="/learn" className="mb-overlay-link" onClick={() => setMenuOpen(false)}>Learn</Link>
                <Link to="/train" className="mb-overlay-link" onClick={() => setMenuOpen(false)}>Train</Link>
                <Link to="/sessions" className="mb-overlay-link" onClick={() => setMenuOpen(false)}>Profit Tracker</Link>
                <Link to="/history/sessions" className="mb-overlay-link" onClick={() => setMenuOpen(false)}>Analysis</Link>
                <Link to="/subscribe" className="mb-overlay-link" onClick={() => setMenuOpen(false)}>Subscribe</Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


